import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ws } from "@/lib/ws";
import type { JobMeta } from "@shared/protocol";
import { relativeTime } from "@/lib/utils";
import { isBinaryExpired } from "@/lib/jobs";
import {
  COMPILED_LINES_TOOLTIP,
  DynamicCodeBadge,
  LINES_OF_CODE_TOOLTIP,
  fmtLines,
} from "@/components/CodeMetrics";

/** Per-row download: request a fresh presigned URL and open it the moment
 *  it arrives — never cached/reused (R2 presigned URLs are short-lived by
 *  design; a fresh one is requested on every click). */
function BinaryCell({ job }: { job: JobMeta }) {
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!pending) return;
    return ws.on("jobs.downloadUrl", (msg) => {
      if (msg.job_id !== job.id) return;
      setPending(false);
      window.open(msg.url, "_blank", "noopener");
    });
  }, [pending, job.id]);

  if (job.status !== "succeeded")
    return <span className="text-sm text-muted-foreground">—</span>;
  if (isBinaryExpired(job)) return <Badge variant="secondary">Expired</Badge>;
  return (
    <button
      type="button"
      className="text-sm text-primary underline disabled:opacity-50"
      disabled={pending}
      onClick={() => {
        setPending(true);
        ws.send({ type: "jobs.download", job_id: job.id });
      }}
    >
      {pending ? "preparing…" : "Download"}
    </button>
  );
}

function statusVariant(status: JobMeta["status"]) {
  switch (status) {
    case "succeeded":
      return "success" as const;
    case "failed":
      return "destructive" as const;
    case "running":
      return "default" as const;
    default:
      return "secondary" as const;
  }
}

export function JobListPage() {
  const [jobs, setJobs] = useState<JobMeta[]>([]);

  useEffect(() => {
    const off = ws.on("jobs.page", (msg) => setJobs(msg.jobs));
    const offStatus = ws.on("job.status", (msg) => {
      setJobs((prev) =>
        prev.map((j) =>
          j.id === msg.job_id ? { ...j, status: msg.status } : j,
        ),
      );
    });
    ws.send({ type: "jobs.list" });
    return () => {
      off();
      offStatus();
    };
  }, []);

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Jobs</h1>
        <Link
          to="/jobs/new"
          className="inline-flex items-center h-9 px-4 py-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90"
        >
          New job
        </Link>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Recent compile jobs</CardTitle>
        </CardHeader>
        <CardContent>
          {jobs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No jobs yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Entry</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>API key</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead
                    className="text-right cursor-help"
                    title={LINES_OF_CODE_TOOLTIP}
                  >
                    Lines of code
                  </TableHead>
                  <TableHead
                    className="text-right cursor-help"
                    title={COMPILED_LINES_TOOLTIP}
                  >
                    Compiled
                  </TableHead>
                  <TableHead>Dynamic code</TableHead>
                  <TableHead>Binary</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell>
                      <Badge variant={statusVariant(job.status)}>
                        {job.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {job.source_summary}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {job.entry}
                    </TableCell>
                    <TableCell className="text-sm">
                      {job.submitted_by
                        ? (job.submitted_by.name ?? job.submitted_by.email)
                        : "—"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {job.api_key_name ?? "—"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {relativeTime(job.created_at)}
                    </TableCell>
                    <TableCell className="text-sm text-right tabular-nums">
                      {fmtLines(job.total_lines_of_code)}
                    </TableCell>
                    <TableCell className="text-sm text-right tabular-nums">
                      {fmtLines(job.compiled_lines_of_code)}
                    </TableCell>
                    <TableCell>
                      <DynamicCodeBadge value={job.dynamic_code} />
                    </TableCell>
                    <TableCell>
                      <BinaryCell job={job} />
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/jobs/${job.id}`}
                        className="text-sm underline"
                      >
                        View
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
